import { Button } from "@design-system/components/Button";
import { InputError } from "@design-system/components/Form/InputError";
import { InputLabel } from "@design-system/components/Form/InputLabel";
import { TextInput } from "@design-system/components/Form/TextInput";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XIcon,
} from "@design-system/components/Icons";
import { Loader } from "@design-system/components/Loader";
import { useSelect, useSelectOption } from "@design-system/hooks";
import type { SelectOption, SelectOptionsGroup } from "@design-system/types";
import { Checkbox } from "@design-system/ui/checkbox";
import { cn } from "@design-system/utils";
import { useIsRTL } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { forwardRef, type RefObject, useRef } from "react";
import { FixedSizeList as List } from "react-window";

export function SelectItem({
  option,
  className,
}: {
  option: SelectOption;
  className?: string;
}) {
  const {
    ref,
    multiple,
    isSelected,
    selectOption,
    isDisabled,
    isHighlighted,
    disabled,
  } = useSelectOption(option);

  const id = `option-${option.value}-input`;

  const isRTLDirection = useIsRTL();

  return (
    <div
      id={id}
      ref={ref}
      onClick={selectOption}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors duration-200",
        isRTLDirection && "flex-row-reverse",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        !multiple && isSelected && "bg-accent text-accent-foreground dark:bg-gray-700 dark:text-gray-100",
        !multiple && "hover:bg-accent hover:text-accent-foreground dark:hover:bg-gray-700 dark:hover:text-gray-100",
        className,
        isHighlighted && "bg-accent text-accent-foreground dark:bg-gray-700 dark:text-gray-100",
        isDisabled && "pointer-events-none opacity-50",
      )}
      data-disabled={isDisabled}
      data-selected={isSelected}
      aria-hidden
    >
      {multiple ? (
        <div className="flex w-full items-center gap-2">
          <Checkbox
            checked={isSelected}
            disabled={disabled || isDisabled}
            className="data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
          />
          <label htmlFor={id} className="flex-1 cursor-pointer">
            {option.label}
          </label>
        </div>
      ) : (
        <>
          {option.label}
          {isSelected && (
            <CheckIcon className="ml-auto size-4 text-xs tracking-widest text-muted-foreground" />
          )}
        </>
      )}
    </div>
  );
}

export function SelectList({ options }: { options: SelectOption[] }) {
  const { refs, virtualized } = useSelect();
  const ref = useRef<HTMLDivElement>(null);

  refs.listContainer = ref;

  if (virtualized) {
    return (
      <div ref={ref} className="max-h-48">
        <List
          height={200}
          itemCount={options.length}
          itemSize={35}
          width="100%"
          className="scrollbar-thin"
        >
          {({ index, style }) => (
            <div style={style}>
              <SelectItem key={options[index].value} option={options[index]} />
            </div>
          )}
        </List>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="scrollbar-thin flex max-h-48 flex-col gap-1 overflow-y-auto"
    >
      {options.map((option) => (
        <SelectItem key={option.value} option={option} />
      ))}
    </div>
  );
}

export function SelectGroup({ group }: { group: SelectOptionsGroup }) {
  const { refs } = useSelect();
  const ref = useRef<HTMLDivElement | null>(null);

  if (!group.label) {
    return <SelectList options={group.options} />;
  }

  if (!refs.groups.includes(ref as RefObject<HTMLDivElement>)) {
    refs.groups.push(ref as RefObject<HTMLDivElement>);
  }

  return (
    <>
      <div ref={ref} className="my-1 px-2 text-sm font-bold text-slate-600 dark:text-slate-300">
        {group.label}
      </div>
      <div className="px-2">
        <SelectList options={group.options} />
      </div>
    </>
  );
}

export function SelectInputLabel() {
  const { open, props } = useSelect();

  const { label, required } = props as unknown as {
    label: string;
    required: boolean;
  };

  if (!label) return null;

  return (
    <InputLabel required={required} onClick={open}>
      {label}
    </InputLabel>
  );
}

export function SelectInputClear() {
  const { changeValue, multiple, value, clearable } = useSelect();

  if (
    !clearable ||
    !value ||
    (multiple && Array.isArray(value) && value.length === 0)
  )
    return null;

  return (
    <XIcon
      role="button"
      aria-label="Clear"
      className="ml-auto h-4 w-4 shrink-0 opacity-50"
      onClick={(e) => {
        e.stopPropagation();
        changeValue(multiple ? [] : "");
      }}
    />
  );
}

export function useSelectHeadingContent() {
  const { selectedOption, selectedOptions, textList, value, multiple, props } =
    useSelect();

  if (!value || (multiple && Array.isArray(value) && value.length === 0)) {
    return textList.placeholder;
  }

  if (multiple) {
    if (props.renderSelectedOptions) {
      return props.renderSelectedOptions(selectedOptions!);
    }

    return trans("form.totalSelected", {
      count: value.length,
    });
  }

  if (props.renderSelectedOption) {
    return props.renderSelectedOption(selectedOption!);
  }

  return selectedOption?.label;
}

/**
 * Select trigger button. Rendered through the Radix trigger's `asChild`, so
 * it must forward the ref (popper anchor) and the trigger props it receives.
 */
export const SelectInputHeading = forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof Button>
>(function SelectInputHeading(props, ref) {
  const { opened, classes, isLoading, props: selectProps } = useSelect();

  const Caret = opened ? ChevronUpIcon : ChevronDownIcon;

  const content = useSelectHeadingContent();

  return (
    <Button
      {...props}
      ref={ref}
      variant="outline"
      role="combobox"
      disabled={selectProps.disabled}
      aria-expanded={opened}
      className={cn(
        "w-full justify-between transition-all duration-300 hover:shadow-md focus:ring-2 focus:ring-primary/20",
        "bg-background hover:bg-accent/50 dark:bg-gray-800 dark:hover:bg-gray-700",
        "border-input hover:border-primary/50 dark:border-gray-600 dark:hover:border-gray-500",
        "text-foreground dark:text-gray-100",
        classes?.heading
      )}
    >
      <div className="flex items-center gap-2">
        {isLoading && <Loader size={4} />}
        {content}
      </div>
      {/* Clearable icon */}
      <SelectInputClear />
      {/* Caret icon */}
      <Caret className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );
});

export function SelectInputError() {
  const { error } = useSelect();

  return <InputError error={error} />;
}

export function SelectInputSearch() {
  const {
    searchable,
    searchValue,
    highlightOptionEvent,
    searchOptionsCallback,
    textList,
  } = useSelect();

  const isRTL = useIsRTL();

  if (!searchable) return null;

  return (
    <div className="mx-1 my-2" style={{ direction: isRTL ? "rtl" : "ltr" }}>
      <TextInput
        name=""
        keepErrorVisible={false}
        value={searchValue}
        onKeyDown={highlightOptionEvent}
        onChange={searchOptionsCallback}
        placeholder={textList.searchPlaceholder}
      />
    </div>
  );
}

export function SelectInputEmpty() {
  const { displayedOptions, textList } = useSelect();

  if (displayedOptions.length > 0) return null;

  return <div className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">{textList.noResults}.</div>;
}

export function SelectInputLoading() {
  const { textList, isLoading } = useSelect();

  if (!isLoading) return null;

  return (
    <div className="my-1 flex items-center gap-2 p-1 px-2">
      <Loader />
      <span className="text-sm text-gray-500 dark:text-gray-400">{textList.loading}</span>
    </div>
  );
}

export function SelectInputGroups() {
  const { groups } = useSelect();
  return (
    <>
      {groups.map((group) => (
        <SelectGroup group={group} key={group.label} />
      ))}
    </>
  );
}
